import { useEffect, useId, useState, type FormEvent } from 'react';
import { AuthFlowError, useAuth, type AuthErrorCode } from '../auth/AuthContext';
import { Spinner } from '../components/Spinner';
import logoColor from '../assets/ambec-logo-color.svg';
import fotoCactus from '../assets/login-solar-cactus.jpg';

const MENSAJES: Record<AuthErrorCode, string> = {
  credenciales: 'Correo o contraseña incorrectos.',
  inactive: 'Tu cuenta está desactivada. Contacta al administrador.',
  no_profile: 'Tu cuenta no tiene un perfil asignado. Contacta al administrador.',
  red: 'No pudimos conectar con el servidor. Inténtalo de nuevo en unos minutos.',
};

/** Clase compartida de los inputs: 44px de alto, radio 10px, foco azul sobrio. */
const INPUT =
  'respira-foco h-11 w-full rounded-[10px] border border-slate-300 bg-white px-3.5 text-[15px] font-normal ' +
  'text-azul-noche outline-none transition-colors duration-150 ease-out placeholder:text-gris/45 ' +
  'hover:border-slate-400 focus:border-azul-profundo';

/**
 * Acceso a la plataforma privada de AMBEC — split clásico de autenticación:
 * columna izquierda blanca (logo arriba, formulario centrado, footer abajo),
 * columna derecha fotográfica de borde a borde (sin textos sobre la foto).
 * En mobile queda solo la columna del formulario. Foto: assets/CREDITOS-IMAGENES.md.
 */
export default function Login() {
  const { signIn, notice, clearNotice } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verPassword, setVerPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorLocal, setErrorLocal] = useState<AuthErrorCode | null>(null);

  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  // Si el backend forzó el cierre de sesión (cuenta desactivada / sin perfil),
  // el aviso llega por el contexto: se muestra y se libera el botón.
  useEffect(() => {
    if (notice) setEnviando(false);
  }, [notice]);

  const error = errorLocal ?? notice;
  const mensajeError = error ? MENSAJES[error] : null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (enviando) return;
    setErrorLocal(null);
    clearNotice();
    setEnviando(true);
    try {
      await signIn(email.trim(), password);
      // Éxito: el AuthProvider valida el perfil y /login redirige por rol.
    } catch (err) {
      setErrorLocal(err instanceof AuthFlowError ? err.code : 'red');
      setEnviando(false);
    }
  }

  return (
    <div className="relative flex min-h-dvh flex-col bg-white">
      {/* Fondo full-bleed: la mitad derecha del viewport es fotográfica (solo desktop).
          El fondo llega al borde de la pantalla; el CONTENIDO no (ver container abajo). */}
      <div
        className="absolute inset-y-0 right-0 hidden w-1/2 overflow-hidden lg:block"
        aria-hidden="true"
      >
        <img
          src={fotoCactus}
          alt=""
          className="anim-kenburns absolute inset-0 h-full w-full object-cover"
        />
        {/* Costura firma: el degradado corporativo donde el blanco toca la foto */}
        <div className="absolute inset-y-0 left-0 z-10 w-[3px] bg-gradient-to-b from-azul-profundo via-azul-medio to-celeste" />
      </div>

      {/* Contenido SIEMPRE dentro del container padre global (máx. 1280px centrado):
          en pantallas extra-wide los textos/logos no se desparraman hacia los bordes. */}
      <div className="relative mx-auto flex w-full max-w-[1280px] flex-1">
        {/* ————— Columna formulario: centro / footer ————— */}
        <div className="flex w-full flex-col px-6 sm:px-10 lg:w-1/2 lg:pr-14">
          <main className="flex flex-1 items-center py-12">
            {/* En desktop el bloque se arrima a la foto (ml-auto); en mobile va centrado */}
            <div className="mx-auto w-full max-w-[384px] lg:ml-auto lg:mr-0">
              {/* Logo centrado sobre el título */}
              <img src={logoColor} alt="AMBEC" className="anim-entrada mx-auto h-8 w-auto" />

              <h1 className="anim-entrada mt-8 text-2xl font-medium tracking-tight text-azul-noche [animation-delay:80ms]">
                Iniciar sesión
              </h1>
              <p className="anim-entrada mt-2 text-sm font-normal leading-relaxed text-gris [animation-delay:140ms]">
                Accede con la cuenta y credenciales personales.
              </p>

              <form onSubmit={onSubmit} noValidate className="anim-entrada mt-8 flex flex-col gap-4 [animation-delay:200ms]">
                {mensajeError && (
                  <div
                    id={errorId}
                    role="alert"
                    className="rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] font-normal text-red-700"
                  >
                    {mensajeError}
                  </div>
                )}

                <div>
                  <label
                    htmlFor={emailId}
                    className="mb-1.5 block text-[13px] font-medium text-azul-noche"
                  >
                    Correo electrónico
                  </label>
                  <input
                    id={emailId}
                    type="email"
                    name="email"
                    autoFocus
                    autoComplete="email"
                    inputMode="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nombre@empresa.cl"
                    aria-invalid={error === 'credenciales' || undefined}
                    aria-describedby={mensajeError ? errorId : undefined}
                    className={INPUT}
                  />
                </div>

                <div>
                  <label
                    htmlFor={passwordId}
                    className="mb-1.5 block text-[13px] font-medium text-azul-noche"
                  >
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      id={passwordId}
                      type={verPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => setCapsLock(e.getModifierState('CapsLock'))}
                      onKeyUp={(e) => setCapsLock(e.getModifierState('CapsLock'))}
                      onBlur={() => setCapsLock(false)}
                      aria-invalid={error === 'credenciales' || undefined}
                      aria-describedby={mensajeError ? errorId : undefined}
                      className={`${INPUT} pr-11`}
                    />
                    <button
                      type="button"
                      onClick={() => setVerPassword((v) => !v)}
                      aria-label={verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      aria-pressed={verPassword}
                      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-[10px] text-gris/60 transition-colors duration-150 ease-out hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-azul-profundo"
                    >
                      {verPassword ? (
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          className="h-[18px] w-[18px]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.5 10.5 0 0 1 12 19.5C5.5 19.5 2 12 2 12a19.8 19.8 0 0 1 4.22-5.28M9.9 4.74A10.6 10.6 0 0 1 12 4.5c6.5 0 10 7.5 10 7.5a19.9 19.9 0 0 1-2.84 3.94" />
                          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                          <path d="m2 2 20 20" />
                        </svg>
                      ) : (
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          className="h-[18px] w-[18px]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M2 12s3.5-7.5 10-7.5S22 12 22 12s-3.5 7.5-10 7.5S2 12 2 12Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {capsLock && (
                    <p
                      aria-live="polite"
                      className="mt-1.5 flex items-center gap-1.5 text-xs font-normal text-amber-700"
                    >
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        className="h-3.5 w-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m5 12 7-7 7 7M8 12h8v4H8zM8 19h8" />
                      </svg>
                      Bloq Mayús está activado.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={enviando}
                  aria-busy={enviando}
                  className="mt-2 flex h-11 w-full items-center justify-center gap-2.5 rounded-[10px] bg-azul-profundo text-[15px] font-medium text-white transition duration-150 ease-out hover:bg-[#003a7c] hover:shadow-[0_10px_28px_-10px_rgba(0,66,141,0.5)] active:scale-[0.99] motion-reduce:active:scale-100 active:bg-[#00325f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {enviando && <Spinner tamano="xs" tono="blanco" />}
                  {enviando ? 'Ingresando…' : 'Ingresar'}
                </button>
              </form>

              <p className="anim-entrada mt-8 text-[13px] font-normal text-gris [animation-delay:280ms]">
                ¿Problemas para acceder?{' '}
                <span className="text-azul-profundo">Escribe a tu administrador.</span>
              </p>
            </div>
          </main>

        </div>
      </div>
    </div>
  );
}
